import { useLocation, Link } from "wouter";
import {
  LayoutDashboard, BookOpen, FileText, HelpCircle, Layers, Video,
  MessageSquare, Users, LogOut, Building2, Settings, CreditCard,
  Tag, TrendingUp, BadgeCheck, Upload, AlertTriangle, Target, ClipboardList,
} from "lucide-react";
import { useAdminGetMe } from "@workspace/api-client-react";

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { data: user } = useAdminGetMe();

  const handleLogout = () => {
    localStorage.removeItem("ff_admin_token");
    setLocation("/login");
  };

  const navItems = [
    { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { href: "/bancas", label: "Bancas", icon: Building2 },
    { href: "/concursos", label: "Concursos", icon: Target },
    { href: "/disciplinas", label: "Disciplinas", icon: BookOpen },
    { href: "/conteudos", label: "Conteúdos", icon: FileText },
    { href: "/questoes", label: "Questões", icon: HelpCircle },
    { href: "/simulados", label: "Simulados", icon: ClipboardList },
    { href: "/flashcards", label: "Flashcards", icon: Layers },
    { href: "/videos", label: "Vídeos", icon: Video },
    { href: "/importacao", label: "Importação e exportação", icon: Upload },
    { href: "/solicitacoes", label: "Sugestões", icon: MessageSquare },
    { href: "/relatos", label: "Relatos de Erros", icon: AlertTriangle },
    { href: "/usuarios", label: "Alunos e acessos", icon: Users },
    { href: "/configuracoes", label: "Configurações", icon: Settings },
    { href: "/planos", label: "Planos e preços", icon: BadgeCheck, group: "Configurações comerciais" },
    { href: "/cupons", label: "Cupons", icon: Tag, group: "Financeiro" },
    { href: "/financeiro", label: "Financeiro", icon: TrendingUp, group: "Financeiro" },
  ];

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="w-64 border-r bg-card flex flex-col">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <img src="/brand/flashfarma-symbol.png" alt="" className="h-11 w-11 rounded-lg" />
            <h1 className="text-xl font-bold text-primary tracking-tight">FlashFarma</h1>
          </div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Administração</p>
        </div>
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto pb-4">
          {(() => {
            const shownGroups = new Set<string>();
            return navItems.map((item) => {
              const group = (item as { group?: string }).group;
              const isActive = location.startsWith(item.href);
              const showHeader = !!group && !shownGroups.has(group);
              if (showHeader) shownGroups.add(group!);
              return (
                <div key={item.href}>
                  {showHeader && (
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-4 pb-1">{group}</p>
                  )}
                  <Link href={item.href}>
                    <div
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                    </div>
                  </Link>
                </div>
              );
            });
          })()}
        </nav>
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            <div className="truncate">
              <p className="text-sm font-medium truncate">{user?.nome || "Admin"}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email || "Carregando..."}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-muted-foreground hover:text-destructive transition-colors rounded-md hover:bg-muted"
              title="Sair"
              data-testid="btn-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
